"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMainEntity = getMainEntity;
exports.getEntities = getEntities;
exports.formatSql = formatSql;
exports.parseSqlScript = parseSqlScript;
exports.parseSqlStatement = parseSqlStatement;
exports.parseSelectStatement = parseSelectStatement;
exports.parseSelectColumn = parseSelectColumn;
exports.parseSelectTable = parseSelectTable;
exports.parseSelectJoinKind = parseSelectJoinKind;
exports.parseCondition = parseCondition;
exports.parseConditionBool = parseConditionBool;
exports.parseSqlConditionBoolOp = parseSqlConditionBoolOp;
exports.parseConditionExp = parseConditionExp;
exports.parseSqlConditionExpOp = parseSqlConditionExpOp;
exports.parseConditionNull = parseConditionNull;
exports.parseConditionIn = parseConditionIn;
exports.parseValue = parseValue;
const utils_1 = require("@azimutt/utils");
const databaseUtils_1 = require("../databaseUtils");
function getMainEntity(sql) {
    const res = sql.match(new RegExp(insertRegex, 'i'))
        || sql.match(new RegExp(updateRegex, 'i'))
        || sql.match(new RegExp(deleteRegex, 'i'))
        || sql.match(new RegExp(fromRegex, 'i'));
    return res ? extractEntityRegex(res) : undefined;
}
function getEntities(sql) {
    const lower = sql.trim().toLowerCase();
    const matches = lower.startsWith('insert') ? [...sql.matchAll(new RegExp(insertRegex, 'gi'))] :
        lower.startsWith('update') ? [...sql.matchAll(new RegExp(updateRegex, 'gi'))] :
            lower.startsWith('delete') ? [...sql.matchAll(new RegExp(deleteRegex, 'gi'))] :
                lower.startsWith('select') ? [...sql.matchAll(new RegExp(fromRegex, 'gi')), ...sql.matchAll(new RegExp(joinRegex, 'gi'))] : [];
    return matches.map(extractEntityRegex).filter(utils_1.isNotUndefined);
}
const identifierRegex = `["'\`\\[]?\\b\\w+\\b["'\`\\]]?`;
const entityRegex = `(?:${identifierRegex}\\.)?${identifierRegex}`;
const insertRegex = `^INSERT\\s+INTO\\s+(${entityRegex})(?:\\s|\\()`;
const updateRegex = `^UPDATE\\s+(${entityRegex})\\s`;
const deleteRegex = `^DELETE\\s+FROM\\s+(${entityRegex})\\s`;
const fromRegex = `\\s+FROM\\s+(${entityRegex})(?:\\s|;)`;
const joinRegex = `\\s+JOIN\\s+(${entityRegex})\\s`;
function extractEntityRegex(res) {
    const [, value] = res;
    const [entity, schema] = value?.replaceAll(/["'`\[\]]/g, '')?.split('.')?.reverse() || [];
    return entity ? (0, utils_1.removeUndefined)({ entity, schema }) : undefined;
}
function formatSql(sql) {
    const maxLength = 200;
    const singleLine = sql.trim().split('\n')
        .map(line => line.replaceAll(/\s+/g, ' ').replaceAll(/--.*/g, '').trim())
        .map((line, i) => i === 0 || line.startsWith(',') ? line : ' ' + line)
        .join('');
    if (singleLine.length > maxLength) {
        const [, select, other] = singleLine.match(/^SELECT\s+(.+)\s+FROM(.*)/) || [];
        return (0, utils_1.maxLen)(select ? `SELECT ${(0, utils_1.maxLen)(select, 40)} FROM${other}` : singleLine, maxLength);
    }
    else {
        return singleLine;
    }
}
function parseSqlScript(script) {
    return script
        .split('\n') // get lines
        .filter(line => !!line.trim() && !line.trim().startsWith('--')) // remove empty & comment lines
        .join('\n')
        .split(';') // get statements (naive)
        .map(s => parseSqlStatement(s.trim() + ';'))
        .filter(utils_1.isNotUndefined);
}
function parseSqlStatement(statement) {
    try {
        if (statement.trim().match(/^SELECT\s+/i)) {
            return parseSelectStatement(statement);
        }
        else {
            return undefined;
        }
    }
    catch {
        return undefined;
    }
}
function parseSelectStatement(statement) {
    const selectRegex = 'SELECT(?:\\s+DISTINCT)?(?:\\s+TOP\\s+(\\d+))?\\s+(.+?)';
    const fromRegex = 'FROM\\s+(.+?)';
    const whereRegex = 'WHERE\\s+(.+?)';
    const groupByRegex = 'GROUP\\s+BY\\s+(.+?)';
    const havingRegex = 'HAVING\\s+(.+?)';
    const orderByRegex = 'ORDER\\s+BY\\s+(.+?)';
    const limitRegex = 'LIMIT\\s+(\\d+)';
    const offsetRegex = 'OFFSET\\s+(\\d+)(?:\\s+ROWS?)?';
    const fetchRegex = 'FETCH\\s+(?:FIRST|NEXT)\\s+(\\d+)\\s+ROWS?\\s+ONLY';
    const optRegex = [whereRegex, groupByRegex, havingRegex, orderByRegex, limitRegex, offsetRegex, fetchRegex].map(r => `(?:\\s+${r})?`).join('');
    const regex = new RegExp(`^${selectRegex}\\s+${fromRegex}${optRegex}\\s*;$`, 'i');
    const [, top, select, from, where, groupBy, having, orderBy, limit, offset, fetch] = statement.trim().match(regex) || [];
    const parsedColumns = (select || '').split(',').map((c, i) => parseSelectColumn(c.trim(), i + 1)).filter(c => !!c);
    const parsedTable = parseSelectTable(from || '');
    if (parsedTable && parsedColumns.length > 0) {
        return (0, utils_1.removeEmpty)({
            command: 'SELECT',
            table: parsedTable.table,
            columns: parsedColumns,
            joins: parsedTable.joins,
            where: where && parseCondition(where),
            groupBy,
            having: having && parseCondition(having),
            orderBy,
            offset: offset ? parseInt(offset) : undefined,
            limit: limit ? parseInt(limit) : fetch ? parseInt(fetch) : top ? parseInt(top) : undefined
        });
    }
    else {
        return undefined;
    }
}
function parseSelectColumn(column, index) {
    const [, scope, def, alias] = column.match(/^(?:(.+?)\.)?(.+?)(?:\s+AS\s+(.+?))?$/i) || [];
    if (def?.match(/^([a-zA-Z0-9_$#]+?|["`].+?["`]?)$/i)) { // simple column
        return (0, utils_1.removeUndefined)({ name: (0, databaseUtils_1.removeQuotes)(alias || def), scope: scope ? (0, databaseUtils_1.removeQuotes)(scope) : undefined, col: [(0, databaseUtils_1.removeQuotes)(def)] });
    }
    else if (def === '*') {
        return (0, utils_1.removeUndefined)({ name: '*', scope: scope ? (0, databaseUtils_1.removeQuotes)(scope) : undefined });
    }
    else {
        return (0, utils_1.removeUndefined)({ name: (0, databaseUtils_1.removeQuotes)(alias || `col_${index}`), def: def ? (0, databaseUtils_1.removeQuotes)(def) : undefined });
    }
}
function parseSelectTable(tables) {
    const [table, ...joins] = tables.replaceAll(/((?:INNER|LEFT|RIGHT|FULL)(?:\s+OUTER)?\s+)?JOIN\s+/gi, 'JOIN $1').split(/JOIN\s+/i) || [];
    const [, tableSchema, tableName, tableAlias] = ((table || '').trim().match(/^(?:(.+?)\.)?(.+?)(?:\s+(.+?))?$/i) || []).map(r => r ? (0, databaseUtils_1.removeQuotes)(r) : undefined);
    if (tableName) {
        const tableFormatted = (0, utils_1.removeUndefined)({ name: tableName, schema: tableSchema, alias: tableAlias });
        const joinsFormatted = joins.map(j => {
            const [, kind, schema, table, alias, on] = (j.trim().match(/^((?:INNER|LEFT|RIGHT|FULL)(?:\s+OUTER)?\s+)?(?:(.+?)\.)?(.+?)(?:\s+(.+?))?\s+ON\s+(.+?)$/i) || []).map(r => r ? (0, databaseUtils_1.removeQuotes)(r) : undefined);
            return table ? (0, utils_1.removeUndefined)({ table, schema, alias, kind: parseSelectJoinKind(kind || ''), on: parseCondition(on || '') }) : undefined;
        }).filter(j => !!j);
        return (0, utils_1.removeEmpty)({ table: tableFormatted, joins: joinsFormatted });
    }
    else {
        return undefined;
    }
}
function parseSelectJoinKind(kind) {
    if (kind.match(/^INNER\s+/i))
        return 'INNER';
    if (kind.match(/^LEFT\s+/i))
        return 'LEFT';
    if (kind.match(/^RIGHT\s+/i))
        return 'RIGHT';
    if (kind.match(/^FULL\s+/i))
        return 'FULL';
    return undefined;
}
function parseCondition(cond) {
    if (cond.search(/AND|OR/i) !== -1) {
        return parseConditionBool(cond);
    }
    else if (cond.search(/[=<>]|\s+LIKE\s+/) !== -1) {
        return parseConditionExp(cond);
    }
    else if (cond.search(/\s+NULL/i) !== -1) {
        return parseConditionNull(cond);
    }
    else if (cond.search(/\s+IN\s+\(/i) !== -1) {
        return parseConditionIn(cond);
    }
    else {
        return cond; // can't parse condition yet :/
    }
}
function parseConditionBool(cond) {
    return cond.replaceAll(/\s+(AND|OR)\s+/gi, '|$1 ').split('|').reduce((acc, item) => {
        if (acc === undefined) {
            return parseCondition(item);
        }
        else {
            const [, opS, rightS] = item.match(/^(AND|OR)\s+(.+?)$/i) || [];
            const op = parseSqlConditionBoolOp(opS);
            const right = parseCondition(rightS);
            return op && right ? { op, left: acc, right } : acc;
        }
    }, undefined) || cond;
}
function parseSqlConditionBoolOp(kind) {
    if (kind.match(/^AND$/i))
        return 'AND';
    if (kind.match(/^OR$/i))
        return 'OR';
    return undefined;
}
function parseConditionExp(cond) {
    const [, leftS, opS, rightS] = cond.match(/^(.+?)\s*(=|!=|>|>=|<|<=|<>|LIKE)\s*(.+?)$/) || [];
    const op = parseSqlConditionExpOp(opS || '');
    const left = parseValue(leftS || '');
    const right = parseValue(rightS || '');
    return op && left !== undefined && right !== undefined ? { op, left, right } : cond;
}
function parseSqlConditionExpOp(kind) {
    if (kind === '=')
        return kind;
    if (kind === '!=')
        return kind;
    if (kind === '>')
        return kind;
    if (kind === '>=')
        return kind;
    if (kind === '<')
        return kind;
    if (kind === '<=')
        return kind;
    if (kind === '<>')
        return kind;
    if (kind === 'LIKE')
        return kind;
    return undefined;
}
function parseConditionNull(cond) {
    const [, valueS, not] = cond.match(/^(.+?)\s+IS\s+(NOT\s+)?NULL$/i) || [];
    const op = (not || '').trim().match(/^NOT$/i) ? 'NOT NULL' : 'NULL';
    const value = parseValue(valueS || '');
    return value ? { op, value } : cond;
}
function parseConditionIn(cond) {
    const [, valueS, not, valuesS] = cond.match(/^(.+?)\s+(NOT\s+)?IN\s+\((.+?)\)$/i) || [];
    const op = (not || '').trim().match(/^NOT$/i) ? 'NOT IN' : 'IN';
    const value = parseValue(valueS || '');
    const values = valuesS.split(',').map(v => parseValue(v.trim())).filter(utils_1.isNotUndefined);
    return value ? { op, value, values } : cond;
}
function parseValue(value) {
    let res = value.match(/^(\d+)$/);
    if (res)
        return parseInt(res[1]);
    res = value.match(/^'([^']+)'$/);
    if (res)
        return res[1];
    res = value.match(/^(?:(.+?)\.)?([a-zA-Z0-9_$#]+?|["`].+?["`])$/i);
    if (res)
        return (0, utils_1.removeUndefined)({ column: (0, databaseUtils_1.removeQuotes)(res[2]), scope: res[1] ? (0, databaseUtils_1.removeQuotes)(res[1]) : undefined });
    return undefined;
}
//# sourceMappingURL=sql.js.map